<template>
  <div class="space-y-6">
    <!-- Sounds -->
    <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
      <h3 class="text-sm font-semibold text-stone-700 dark:text-stone-300 mb-2">
        {{ t('optionsdialog.Sounds') }}
      </h3>
      <DuringSoundsInner
        :duringSpinSound="wheelConfig.duringSpinSound"
        :duringSpinSoundVolume="wheelConfig.duringSpinSoundVolume"
        @set-during-spin-sound="$emit('set-during-spin-sound', $event)"
        @set-during-spin-sound-volume="$emit('set-during-spin-sound-volume', $event)"
      />
    </div>

    <!-- Behavior -->
    <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
      <h3 class="text-sm font-semibold text-stone-700 dark:text-stone-300 mb-3">
        {{ t('optionsdialog.Behavior') }}
      </h3>
      <div class="grid gap-3 md:grid-cols-2">
        <label class="flex items-start gap-3">
          <input id="allowDuplicates" type="checkbox" v-model="wheelConfig.allowDuplicates"
            class="form-checkbox h-4 w-4 mt-0.5 text-primary-500 rounded focus:ring-primary-500 border-stone-300 dark:border-stone-600" />
          <div class="min-w-0">
            <div class="text-stone-700 dark:text-stone-300 flex items-center gap-2">
              <span>{{ t('optionsdialog.Display duplicates') }}</span>
              <tooltip :title="t('optionsdialog.Display every entry')" />
            </div>
          </div>
        </label>

        <label class="flex items-start gap-3">
          <input id="slowSpin" type="checkbox" v-model="wheelConfig.slowSpin"
            class="form-checkbox h-4 w-4 mt-0.5 text-primary-500 rounded focus:ring-primary-500 border-stone-300 dark:border-stone-600" />
          <div class="min-w-0 text-stone-700 dark:text-stone-300">
            {{ t('optionsdialog.Spin slowly') }}
          </div>
        </label>

        <label class="flex items-start gap-3">
          <input id="showTitle" type="checkbox" v-model="wheelConfig.showTitle"
            class="form-checkbox h-4 w-4 mt-0.5 text-primary-500 rounded focus:ring-primary-500 border-stone-300 dark:border-stone-600" />
          <div class="min-w-0 text-stone-700 dark:text-stone-300">
            {{ t('optionsdialog.Show title') }}
          </div>
        </label>
      </div>
  </div>

    <!-- Spin time -->
    <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
      <label for="spinTime" class="block text-sm font-semibold text-stone-700 dark:text-stone-300 mb-1">
        {{ t('optionsdialog.Spin time (seconds)') }}
      </label>
      <div class="grid grid-cols-[auto,1fr,auto] items-center gap-3">
        <span class="text-xs text-stone-500 dark:text-stone-400">1</span>
        <input id="spinTime" type="range" v-model.number="wheelConfig.spinTime" min="1" max="60" step="1"
          class="w-full h-2 bg-stone-200 dark:bg-stone-700 rounded-lg appearance-none cursor-pointer accent-primary-500" />
        <span class="text-xs text-stone-500 dark:text-stone-400">60</span>
      </div>
      <div class="text-right text-sm font-medium text-stone-600 dark:text-stone-400 mt-1">
        {{ wheelConfig.spinTime }}
      </div>
    </div>

    <!-- Max names -->
    <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
      <div class="flex items-center gap-2 mb-1">
        <label for="maxNames" class="text-sm font-semibold text-stone-700 dark:text-stone-300">
          {{ t('optionsdialog.Max names on wheel') }}
        </label>
        <tooltip v-if="entriesHaveUnequalWeight" :title="t('optionsdialog.Disabled because the entries')" />
      </div>
      <p class="text-xs text-stone-500 dark:text-stone-400 mb-2">
        {{ t('optionsdialog.All names in the text-box') }}
      </p>
      <div class="grid grid-cols-[auto,1fr,auto] items-center gap-3">
        <span class="text-xs text-stone-500 dark:text-stone-400">4</span>
        <input id="maxNames" type="range" v-model.number="wheelConfig.maxNames" min="4" max="1000" step="1"
          :disabled="entriesHaveUnequalWeight"
          class="w-full h-2 bg-stone-200 dark:bg-stone-700 rounded-lg appearance-none cursor-pointer accent-primary-500 disabled:opacity-50" />
        <span class="text-xs text-stone-500 dark:text-stone-400">1000</span>
      </div>
      <div class="text-right text-sm font-medium text-stone-600 dark:text-stone-400 mt-1">
        {{ wheelConfig.maxNames }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import DuringSoundsInner from '@/components/options/duringSoundsInner.vue';
import tooltip from '@/components/ui/tooltip.vue';
import { computed } from 'vue';

const { wheelConfig } = defineProps({
  wheelConfig: { type: Object, required: true }
});

defineEmits(['set-during-spin-sound', 'set-during-spin-sound-volume']);

const { t } = useI18n();

const entriesHaveUnequalWeight = computed(() => {
  if (!wheelConfig.entries || wheelConfig.entries.length == 0 || !wheelConfig.entries[0].weight) return false;
  return !!wheelConfig.entries.find(entry => entry.weight != 1);
});
</script>
